import { useMemo, useState } from 'react';

import { Layer, Marker, MarkerDragEvent, Source } from 'react-map-gl/mapbox';

import { PostGeoConfigLocation } from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import { createGeoJSONCircleMiles } from 'pages/Settings/GeoConfigSettings/GeoMap';
import GeoMapPinPopup from 'pages/Settings/GeoConfigSettings/GeoMap/GeoMapPinPopup';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import { MapPin } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/MapPin';

import styles from './GeoMapPin.module.scss';

export interface GeoMapPinProps {
  location: PostGeoConfigLocation;
  isSelected: boolean;
  editing: boolean;
  moving: boolean;
  popupPin?: PostGeoConfigLocation;
  handleClick?(location: PostGeoConfigLocation): void;
  handleDragStart?(location: PostGeoConfigLocation): void;
  handleDragEnd?(e: MarkerDragEvent, location: PostGeoConfigLocation): void;
  handleMouseEnter(location: PostGeoConfigLocation): void;
  handleMouseLeave(): void;
}

/**
 * GeoMapPin
 */
export default function GeoMapPin({
  location,
  isSelected,
  editing,
  moving,
  popupPin,
  handleClick,
  handleDragStart,
  handleDragEnd,
  handleMouseEnter,
  handleMouseLeave,
}: GeoMapPinProps) {
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);

  const data = useMemo(
    () => createGeoJSONCircleMiles([location.longitude, location.latitude], location.radius),
    [location.longitude, location.latitude, location.radius],
  );

  if (!validateLocation(location)) {
    return null;
  }

  return (
    <>
      {/*Pin mark*/}
      <Marker
        key={`pin-${location?.id}-${location.name}-${location.latitude}-${location.longitude}`}
        draggable={editing}
        longitude={location.longitude}
        latitude={location.latitude}
        onClick={() => (handleClick ? handleClick(location) : null)}
        onDragStart={() => (handleDragStart ? handleDragStart(location) : null)}
        onDragEnd={(e) => (handleDragEnd ? handleDragEnd(e, location) : null)}
      >
        {/*Create for adding mouseEnter/mouseLeave event and setting anchor for pin popup*/}
        <div
          ref={setAnchorElement}
          onMouseEnter={() => handleMouseEnter(location)}
          onMouseLeave={handleMouseLeave}
          className={styles.markerContainer}
        >
          <MapPin color={isSelected ? COLOR.SPARKY_BLUE_400 : COLOR.SPARKY_GREY_400} />
        </div>
      </Marker>

      {/*Pin popup*/}
      {popupPin?.id === location?.id && popupPin?.name === location.name && popupPin && (
        <GeoMapPinPopup anchorElement={anchorElement} location={popupPin} editing={editing} />
      )}

      {/*Radius layer*/}
      {!moving && (
        <Source
          key={`polygons-${location?.id}-${location.name}-${location.latitude}-${location.longitude}`}
          id={`polygons-${location?.id}-${location.name}-${location.latitude}-${location.longitude}`}
          type='geojson'
          data={data}
        >
          <Layer
            id={`circles-${location?.id}-${location.name}-${location.latitude}-${location.longitude}`}
            type='fill'
            paint={{
              'fill-outline-color': isSelected ? '#86B3FB' : '#C3C3C3',
              'fill-color': isSelected ? '#86B3FB' : '#C3C3C3',
              'fill-opacity': 0.2,
            }}
          />
          <Layer
            id={`borders-${location?.id}-${location.name}-${location.latitude}-${location.longitude}`}
            type='line'
            paint={{
              'line-offset': editing ? 5 : 0,
              'line-color': isSelected ? '#0D67F8' : '#C3C3C3',
              'line-width': 2,
            }}
          />
          {editing && (
            <Layer
              id={`strokes-${location?.id}-${location.name}-${location.latitude}-${location.longitude}`}
              type='line'
              paint={{
                'line-color': '#86B3FB',
                'line-width': 8,
              }}
            />
          )}
        </Source>
      )}
    </>
  );
}

function validateLocation(location: PostGeoConfigLocation) {
  const { latitude, longitude } = location;
  return longitude <= 180 && longitude >= -180 && latitude <= 90 && latitude >= -90;
}
